// Signed-in state of the showroom (HIA-BUG-20260929-4, Trello 27MN7nRG).
//
// Single source of truth: the Redux slice `authentication.session = {mode, displayName} | null`,
// mirrored in sessionStorage so the native modes survive a reload (they have no server session:
// the native "sign-in" is a demo UI state). The mirror holds ONLY {mode, displayName}: no token,
// no challenge id, no user id.

export const MODE_NATIVE_ID = 'native-id';
export const MODE_NATIVE_QR = 'native-qr';
export const MODE_KEYCLOAK = 'keycloak';

export const SESSION_STORAGE_KEY = 'showroom.session';

// Keys written by the previous versions of the app; never read any more.
const LEGACY_KEYS = ['loggedIn', 'Challenge'];

const MODES = [MODE_NATIVE_ID, MODE_NATIVE_QR, MODE_KEYCLOAK];

export const buildSession = (mode, displayName) => ({
    mode,
    displayName: typeof displayName === 'string' ? displayName : '',
});

const isValidSession = (value) =>
    !!value && typeof value === 'object' && MODES.indexOf(value.mode) !== -1 && typeof value.displayName === 'string';

export function readStoredSession() {
    try {
        const parsed = JSON.parse(window.sessionStorage.getItem(SESSION_STORAGE_KEY));
        return isValidSession(parsed) ? buildSession(parsed.mode, parsed.displayName) : null;
    } catch (_) {
        return null;
    }
}

export function storeSession(session) {
    try {
        window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(buildSession(session.mode, session.displayName)));
    } catch (_) {
        // Storage unavailable (private mode...): the in-memory state still applies.
    }
}

export function clearStoredSession() {
    try {
        window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (_) {
        // ignore
    }
}

// Drop the keys of the previous versions so an old `loggedIn=true` never signs anybody in.
export function purgeLegacyKeys() {
    LEGACY_KEYS.forEach((key) => {
        try {
            window.sessionStorage.removeItem(key);
            window.localStorage.removeItem(key);
        } catch (_) {
            // ignore
        }
    });
}

// Session restorable at boot from the mirror. A Keycloak session is NEVER restored from storage: the mirror
// can be stale (SSO session closed elsewhere) or forged. It is set again only once keycloak-js' silent
// check-sso succeeds (src/index.js). The native modes have no server session and are restored as is.
export function readRestorableSession() {
    const stored = readStoredSession();
    return stored && stored.mode !== MODE_KEYCLOAK ? stored : null;
}

export const ROUTE_WORKSPACE = 'workspace';
export const ROUTE_WAIT = 'wait';
export const ROUTE_SIGN_IN = 'sign-in';

// Guard of the workspace route (/admin):
// - a native session opens the workspace;
// - a Keycloak session opens it only while keycloak-js really holds a session (token), never on the mirror alone;
// - otherwise wait for the silent Keycloak check (it may sign the user in, e.g. the callback on /admin), then
//   send the user to the sign-in page.
export function resolveWorkspaceRoute(session, ssoReady, keycloakAuthenticated) {
    if (session && (session.mode !== MODE_KEYCLOAK || keycloakAuthenticated)) {
        return ROUTE_WORKSPACE;
    }
    return ssoReady ? ROUTE_SIGN_IN : ROUTE_WAIT;
}
